import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  ImageList,
  ImageListItem,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Visibility,
  ShoppingCart,
  Close,
  PhotoCamera,
  Category as CategoryIcon,
  MoreVert,
  AttachMoney,
  FilterList,
  Print,
  QrCode2,
  Upload,
  Download,
} from '@mui/icons-material';
import { Checkbox } from '@mui/material';
import api from '../services/api';
import BarcodeLabel from '../components/BarcodeLabel';
import LabelPrintDialog from '../components/LabelPrintDialog';
import BulkActionsToolbar from '../components/BulkActionsToolbar';
import ImportDialog from '../components/ImportDialog';

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [saleFilter, setSaleFilter] = useState('');

  // Modals
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [barcodeLabelDialogOpen, setBarcodeLabelDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    price: '',
    original_price: '',
    quantity: 1,
    status: 'available',
    condition: 'good',
    location: '',
    category_id: '',
    sale_id: '',
    brand: '',
    model: '',
    year: '',
    dimensions: '',
    weight: '',
    notes: '',
  });

  // Category form
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Photo upload
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  // Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuItem, setMenuItem] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [itemsRes, categoriesRes, salesRes] = await Promise.all([
        api.get('/inventory/items'),
        api.get('/inventory/categories'),
        api.get('/sales/'),
      ]);

      setItems(itemsRes.data?.items || itemsRes.data || []);
      setCategories(categoriesRes.data?.categories || categoriesRes.data || []);
      setSales(salesRes.data?.sales || salesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category_id', categoryFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (saleFilter) params.append('sale_id', saleFilter);

      const response = await api.get(`/inventory/items?${params.toString()}`);
      setItems(response.data?.items || response.data || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      showSnackbar('Failed to load items', 'error');
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchItems();
    }
  }, [categoryFilter, statusFilter, saleFilter]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenItemDialog = (item = null) => {
    if (item) {
      setFormData(item);
      setEditMode(true);
    } else {
      setFormData({
        name: '',
        description: '',
        sku: '',
        price: '',
        original_price: '',
        quantity: 1,
        status: 'available',
        condition: 'good',
        location: '',
        category_id: '',
        sale_id: '',
        brand: '',
        model: '',
        year: '',
        dimensions: '',
        weight: '',
        notes: '',
      });
      setEditMode(false);
    }
    setItemDialogOpen(true);
  };

  const handleCloseItemDialog = () => {
    setItemDialogOpen(false);
    setSelectedPhotos([]);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();

    try {
      if (editMode) {
        await api.put(`/inventory/items/${formData.id}`, formData);
        showSnackbar('Item updated successfully');
      } else {
        const response = await api.post('/inventory/items', formData);

        // Upload photos if any
        if (selectedPhotos.length > 0 && response.data?.id) {
          await uploadPhotos(response.data.id);
        }

        showSnackbar('Item created successfully');
      }

      handleCloseItemDialog();
      fetchItems();
    } catch (error) {
      console.error('Error saving item:', error);
      showSnackbar(error.response?.data?.message || 'Failed to save item', 'error');
    }
  };

  const uploadPhotos = async (itemId) => {
    try {
      const formData = new FormData();
      selectedPhotos.forEach((photo, index) => {
        formData.append(`photos`, photo);
      });

      await api.post(`/inventory/items/${itemId}/photos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      console.error('Error uploading photos:', error);
      showSnackbar('Item saved but photo upload failed', 'warning');
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedPhotos(files);
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await api.delete(`/inventory/items/${itemId}`);
      showSnackbar('Item deleted successfully');
      fetchItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      showSnackbar('Failed to delete item', 'error');
    }
  };

  const handleMarkAsSold = async (itemId) => {
    try {
      await api.post(`/inventory/items/${itemId}/sell`, {
        sold_date: new Date().toISOString(),
      });
      showSnackbar('Item marked as sold');
      fetchItems();
      handleCloseMenu();
    } catch (error) {
      console.error('Error marking item as sold:', error);
      showSnackbar('Failed to mark item as sold', 'error');
    }
  };

  const handleViewDetails = async (item) => {
    try {
      const response = await api.get(`/inventory/items/${item.id}`);
      setSelectedItem(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Error fetching item details:', error);
      showSnackbar('Failed to load item details', 'error');
    }
  };

  const handleOpenMenu = (event, item) => {
    setAnchorEl(event.currentTarget);
    setMenuItem(item);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuItem(null);
  };

  const handleOpenCategoryDialog = () => {
    setCategoryForm({ name: '', description: '' });
    setCategoryDialogOpen(true);
  };

  const handleCloseCategoryDialog = () => {
    setCategoryDialogOpen(false);
  };

  const handleCategoryFormChange = (e) => {
    const { name, value } = e.target;
    setCategoryForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitCategory = async (e) => {
    e.preventDefault();

    try {
      await api.post('/inventory/categories', categoryForm);
      showSnackbar('Category created successfully');
      handleCloseCategoryDialog();

      // Refresh categories
      const response = await api.get('/inventory/categories');
      setCategories(response.data?.categories || response.data || []);
    } catch (error) {
      console.error('Error creating category:', error);
      showSnackbar('Failed to create category', 'error');
    }
  };

  const handlePrintLabel = (item) => {
    setSelectedItem(item);
    setSelectedItems([item]);
    setLabelDialogOpen(true);
    handleCloseMenu();
  };

  const handlePrintSelectedLabels = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Please select items to print labels', 'warning');
      return;
    }
    setLabelDialogOpen(true);
  };

  const handleViewBarcode = (item) => {
    setSelectedItem(item);
    setBarcodeLabelDialogOpen(true);
    handleCloseMenu();
  };

  const handleSelectItem = (item) => {
    setSelectedItems((prev) => {
      const exists = prev.find((i) => i.id === item.id);
      if (exists) {
        return prev.filter((i) => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleSelectAllItems = (checked) => {
    if (checked) {
      setSelectedItems(filteredItems);
    } else {
      setSelectedItems([]);
    }
  };

  const isItemSelected = (itemId) => {
    return selectedItems.some((item) => item.id === itemId);
  };

  const handleBulkUpdate = async (updateData) => {
    try {
      const itemIds = selectedItems.map((item) => item.id);
      await api.post('/inventory/items/bulk-update', {
        item_ids: itemIds,
        ...updateData,
      });
      showSnackbar('Items updated successfully');
      setSelectedItems([]);
      fetchItems();
    } catch (error) {
      console.error('Error updating items:', error);
      showSnackbar(error.response?.data?.message || 'Failed to update items', 'error');
    }
  };

  const handleBulkDelete = async () => {
    try {
      const itemIds = selectedItems.map((item) => item.id);
      await api.post('/inventory/items/bulk-delete', { item_ids: itemIds });
      showSnackbar('Items deleted successfully');
      setSelectedItems([]);
      fetchItems();
    } catch (error) {
      console.error('Error deleting items:', error);
      showSnackbar(error.response?.data?.message || 'Failed to delete items', 'error');
    }
  };

  const handleExportItems = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category_id', categoryFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (saleFilter) params.append('sale_id', saleFilter);

      const response = await api.get(`/inventory/items/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventory_export_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar('Items exported successfully');
    } catch (error) {
      console.error('Error exporting items:', error);
      showSnackbar('Failed to export items', 'error');
    }
  };

  const handleImport = async (formData) => {
    try {
      const response = await api.post('/inventory/items/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      showSnackbar(response.data.message || 'Items imported successfully');
      setImportDialogOpen(false);
      fetchItems();

      return response.data;
    } catch (error) {
      console.error('Error importing items:', error);
      throw error;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'success',
      sold: 'default',
      reserved: 'warning',
      removed: 'error',
    };
    return colors[status] || 'default';
  };

  const getConditionColor = (condition) => {
    const colors = {
      excellent: 'success',
      good: 'info',
      fair: 'warning',
      poor: 'error',
    };
    return colors[condition] || 'default';
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'N/A';
  };

  const getSaleName = (saleId) => {
    const sale = sales.find(s => s.id === saleId);
    return sale?.title || 'N/A';
  };

  const filteredItems = items.filter(item => {
    const matchesSearch =
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" fontWeight="bold">
            Inventory Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and manage sale items
          </Typography>
        </div>
        <Box display="flex" gap={2}>
          {selectedItems.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Print />}
              onClick={handlePrintSelectedLabels}
            >
              Print Labels ({selectedItems.length})
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => setImportDialogOpen(true)}
          >
            Import CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportItems}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<CategoryIcon />}
            onClick={handleOpenCategoryDialog}
          >
            Manage Categories
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenItemDialog()}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="All Items" />
        <Tab label="Categories" />
      </Tabs>

      {tabValue === 0 && (
        <>
          <Card sx={{ mb: 3 }}>
            <Box p={2}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search by name or SKU..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={categoryFilter}
                      label="Category"
                      onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="available">Available</MenuItem>
                      <MenuItem value="sold">Sold</MenuItem>
                      <MenuItem value="reserved">Reserved</MenuItem>
                      <MenuItem value="removed">Removed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Sale</InputLabel>
                    <Select
                      value={saleFilter}
                      label="Sale"
                      onChange={(e) => setSaleFilter(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {sales.map((sale) => (
                        <MenuItem key={sale.id} value={sale.id}>
                          {sale.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<FilterList />}
                    onClick={() => {
                      setCategoryFilter('');
                      setStatusFilter('');
                      setSaleFilter('');
                      setSearchQuery('');
                    }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Card>

          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          filteredItems.length > 0 &&
                          selectedItems.length === filteredItems.length
                        }
                        indeterminate={
                          selectedItems.length > 0 &&
                          selectedItems.length < filteredItems.length
                        }
                        onChange={(e) => handleSelectAllItems(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>Photo</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sale</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.length > 0 ? (
                    filteredItems
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((item) => (
                        <TableRow key={item.id} hover selected={isItemSelected(item.id)}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isItemSelected(item.id)}
                              onChange={() => handleSelectItem(item)}
                            />
                          </TableCell>
                          <TableCell>
                            <Avatar
                              variant="rounded"
                              src={item.photos?.[0]?.url}
                              sx={{ width: 50, height: 50 }}
                            >
                              <PhotoCamera />
                            </Avatar>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {item.name}
                            </Typography>
                            {item.brand && (
                              <Typography variant="caption" color="text.secondary">
                                {item.brand}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {item.sku || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>{getCategoryName(item.category_id)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              ${Number(item.price || 0).toFixed(2)}
                            </Typography>
                            {item.original_price && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textDecoration: 'line-through' }}
                              >
                                ${Number(item.original_price).toFixed(2)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.status}
                              color={getStatusColor(item.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {getSaleName(item.sale_id)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(item)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenMenu(e, item)}
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <Typography variant="body1" color="text.secondary">
                          No items found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Card>
        </>
      )}

      {tabValue === 1 && (
        <Card>
          <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" fontWeight="bold">
                Categories
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleOpenCategoryDialog}
              >
                Add Category
              </Button>
            </Box>
            <Grid container spacing={2}>
              {categories.map((category) => (
                <Grid item xs={12} sm={6} md={4} key={category.id}>
                  <Card variant="outlined">
                    <Box p={2}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <CategoryIcon color="primary" />
                        <Typography variant="h6">{category.name}</Typography>
                      </Box>
                      {category.description && (
                        <Typography variant="body2" color="text.secondary">
                          {category.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        {items.filter(i => i.category_id === category.id).length} items
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
              {categories.length === 0 && (
                <Grid item xs={12}>
                  <Box textAlign="center" py={6}>
                    <Typography variant="body1" color="text.secondary">
                      No categories yet. Create your first category to get started.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Card>
      )}

      {/* Add/Edit Item Dialog */}
      <Dialog
        open={itemDialogOpen}
        onClose={handleCloseItemDialog}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmitItem}>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {editMode ? 'Edit Item' : 'Add New Item'}
              </Typography>
              <IconButton onClick={handleCloseItemDialog}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Item Name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SKU"
                  name="sku"
                  value={formData.sku}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Price"
                  name="price"
                  value={formData.price}
                  onChange={handleFormChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Original Price"
                  name="original_price"
                  value={formData.original_price}
                  onChange={handleFormChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleFormChange}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleFormChange}
                  placeholder="e.g., Living Room, Garage"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category_id"
                    value={formData.category_id}
                    label="Category"
                    onChange={handleFormChange}
                  >
                    <MenuItem value="">None</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Sale</InputLabel>
                  <Select
                    name="sale_id"
                    value={formData.sale_id}
                    label="Sale"
                    onChange={handleFormChange}
                  >
                    <MenuItem value="">None</MenuItem>
                    {sales.map((sale) => (
                      <MenuItem key={sale.id} value={sale.id}>
                        {sale.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    label="Status"
                    onChange={handleFormChange}
                  >
                    <MenuItem value="available">Available</MenuItem>
                    <MenuItem value="sold">Sold</MenuItem>
                    <MenuItem value="reserved">Reserved</MenuItem>
                    <MenuItem value="removed">Removed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Condition</InputLabel>
                  <Select
                    name="condition"
                    value={formData.condition}
                    label="Condition"
                    onChange={handleFormChange}
                  >
                    <MenuItem value="excellent">Excellent</MenuItem>
                    <MenuItem value="good">Good</MenuItem>
                    <MenuItem value="fair">Fair</MenuItem>
                    <MenuItem value="poor">Poor</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Brand"
                  name="brand"
                  value={formData.brand}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Model"
                  name="model"
                  value={formData.model}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Year"
                  name="year"
                  value={formData.year}
                  onChange={handleFormChange}
                  placeholder="e.g., 2020"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Dimensions"
                  name="dimensions"
                  value={formData.dimensions}
                  onChange={handleFormChange}
                  placeholder="e.g., 24x36x12"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Weight"
                  name="weight"
                  value={formData.weight}
                  onChange={handleFormChange}
                  placeholder="e.g., 15 lbs"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleFormChange}
                />
              </Grid>
              {!editMode && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<PhotoCamera />}
                  >
                    Upload Photos
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={handlePhotoChange}
                    />
                  </Button>
                  {selectedPhotos.length > 0 && (
                    <Typography variant="caption" display="block" mt={1}>
                      {selectedPhotos.length} photo(s) selected
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseItemDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Item Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedItem && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{selectedItem.name}</Typography>
                <Box>
                  <IconButton onClick={() => {
                    setDetailDialogOpen(false);
                    handleOpenItemDialog(selectedItem);
                  }}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => {
                    handleDeleteItem(selectedItem.id);
                    setDetailDialogOpen(false);
                  }}>
                    <Delete />
                  </IconButton>
                  <IconButton onClick={() => setDetailDialogOpen(false)}>
                    <Close />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                {selectedItem.photos && selectedItem.photos.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Photos
                    </Typography>
                    <ImageList cols={3} rowHeight={164}>
                      {selectedItem.photos.map((photo, index) => (
                        <ImageListItem key={index}>
                          <img
                            src={photo.url}
                            alt={`${selectedItem.name} ${index + 1}`}
                            loading="lazy"
                            style={{ objectFit: 'cover' }}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    SKU
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {selectedItem.sku || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body1">
                    {getCategoryName(selectedItem.category_id)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Price
                  </Typography>
                  <Typography variant="h6" color="primary">
                    ${Number(selectedItem.price || 0).toFixed(2)}
                  </Typography>
                  {selectedItem.original_price && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textDecoration: 'line-through' }}
                    >
                      ${Number(selectedItem.original_price).toFixed(2)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Box display="flex" gap={1} mt={1}>
                    <Chip
                      label={selectedItem.status}
                      color={getStatusColor(selectedItem.status)}
                      size="small"
                    />
                    <Chip
                      label={selectedItem.condition}
                      color={getConditionColor(selectedItem.condition)}
                      size="small"
                    />
                  </Box>
                </Grid>
                {selectedItem.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {selectedItem.description}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Brand
                  </Typography>
                  <Typography variant="body1">
                    {selectedItem.brand || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Model
                  </Typography>
                  <Typography variant="body1">
                    {selectedItem.model || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Year
                  </Typography>
                  <Typography variant="body1">
                    {selectedItem.year || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Dimensions
                  </Typography>
                  <Typography variant="body1">
                    {selectedItem.dimensions || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Weight
                  </Typography>
                  <Typography variant="body1">
                    {selectedItem.weight || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body1">
                    {selectedItem.location || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Quantity
                  </Typography>
                  <Typography variant="body1">
                    {selectedItem.quantity || 1}
                  </Typography>
                </Grid>
                {selectedItem.sale_id && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Sale
                    </Typography>
                    <Typography variant="body1">
                      {getSaleName(selectedItem.sale_id)}
                    </Typography>
                  </Grid>
                )}
                {selectedItem.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {selectedItem.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              {selectedItem.status !== 'sold' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ShoppingCart />}
                  onClick={() => handleMarkAsSold(selectedItem.id)}
                >
                  Mark as Sold
                </Button>
              )}
              <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={handleCloseCategoryDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmitCategory}>
          <DialogTitle>Add New Category</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Category Name"
                  name="name"
                  value={categoryForm.name}
                  onChange={handleCategoryFormChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  name="description"
                  value={categoryForm.description}
                  onChange={handleCategoryFormChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCategoryDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              Create
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => {
          handleViewDetails(menuItem);
          handleCloseMenu();
        }}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          handleOpenItemDialog(menuItem);
          handleCloseMenu();
        }}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleViewBarcode(menuItem)}>
          <ListItemIcon>
            <QrCode2 fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Barcode/QR</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handlePrintLabel(menuItem)}>
          <ListItemIcon>
            <Print fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print Label</ListItemText>
        </MenuItem>
        <Divider />
        {menuItem?.status !== 'sold' && (
          <MenuItem onClick={() => handleMarkAsSold(menuItem.id)}>
            <ListItemIcon>
              <ShoppingCart fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mark as Sold</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          handleDeleteItem(menuItem.id);
          handleCloseMenu();
        }} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Label Print Dialog */}
      <LabelPrintDialog
        open={labelDialogOpen}
        onClose={() => setLabelDialogOpen(false)}
        items={selectedItems}
      />

      {/* Barcode/QR Code Dialog */}
      <Dialog
        open={barcodeLabelDialogOpen}
        onClose={() => setBarcodeLabelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Barcode & QR Code</Typography>
            <IconButton onClick={() => setBarcodeLabelDialogOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedItem && <BarcodeLabel item={selectedItem} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBarcodeLabelDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Actions Toolbar */}
      <BulkActionsToolbar
        selectedItems={selectedItems}
        onDeselectAll={() => setSelectedItems([])}
        categories={categories}
        sales={sales}
        onBulkUpdate={handleBulkUpdate}
        onBulkDelete={handleBulkDelete}
        onPrintLabels={handlePrintSelectedLabels}
      />

      {/* Import Dialog */}
      <ImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImport}
        sales={sales}
        categories={categories}
      />
    </Box>
  );
};

export default Inventory;
